import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { IonContent, IonMenu, IonMenuToggle, IonRouterLink, IonRouterOutlet, IonSplitPane } from '@ionic/angular/standalone';
import { ReactiveFormsModule } from '@angular/forms';
import { Store } from '@ngxs/store';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { lastValueFrom } from 'rxjs';
import { StateReset } from 'ngxs-reset-plugin';
import { WorkAuthenticationV2Service, WorkPermissionsEnum, WorkUser } from '@work-frontend-monorepo/work-rest-api';
import { ButtonComponent } from '@work-frontend-monorepo/work-ui/layout';
import { InfoblockComponent } from '@work-frontend-monorepo/work-ui/data-display';
import { PrependServerToFileSrcPipe } from '@work-frontend-monorepo/work-ui/pipes';
import { APP_LANGUAGE_OPTIONS, ExceptionHandlingService, LanguageService } from '@work-frontend-monorepo/work-ui/services';
import { SelectComponent } from '@work-frontend-monorepo/work-ui/forms';
import { AppLanguageService } from '../../../../global/services/app-language.service';
import { ProfileState } from '@work-frontend-monorepo/work-ui/state-management';
import { WorksuiteAdminConfigService, WorksuiteModuleKeyEnum } from '@work-frontend-monorepo/work-environments';

@Component({
  selector: 'sfg-app-mitarbeitende-menu-page',
  templateUrl: './mitarbeitende-menu-page.component.html',
  styleUrls: ['./mitarbeitende-menu-page.component.scss'],
  standalone: true,
  imports: [
    IonContent,
    ReactiveFormsModule,
    IonSplitPane,
    IonMenu,
    IonRouterOutlet,
    RouterLink,
    RouterLinkActive,
    IonMenuToggle,
    ButtonComponent,
    IonRouterLink,
    InfoblockComponent,
    PrependServerToFileSrcPipe,
    SelectComponent,
  ],
})
export class MitarbeitendeMenuPageComponent implements OnInit {
  private static readonly DEVICE_LANGUAGE = 'device';

  public mitarbeitendeRoutes = [
    { title: 'HOME', url: '/mitarbeitende/home', icon: 'home' },
    { title: 'ADRESSBUCH', url: '/mitarbeitende/adressbuch', icon: 'group' },
    { title: 'MITTEILUNGEN', url: '/mitarbeitende/mitteilungen', icon: 'newsmode' },
    { title: 'ARBEITSZEIT', url: '/mitarbeitende/arbeitszeit', icon: 'schedule' },
    { title: 'PROJEKTE', url: '/mitarbeitende/projekte', icon: 'folder_copy' },
    { title: 'SCAN_UND_UPLOAD', url: '/mitarbeitende/scan-und-upload', icon: 'document_scanner' },
    { title: 'ABSENZEN', url: '/mitarbeitende/absenzen', icon: 'beach_access' },
    // { title: 'PROFIL', url: '/mitarbeitende/profil', icon: 'frame_person' },
  ];

  private readonly store = inject(Store);
  private readonly workAuthenticationV2Service = inject(WorkAuthenticationV2Service);
  private readonly exceptionHandlingService = inject(ExceptionHandlingService);
  private readonly router = inject(Router);
  private readonly appLanguageService = inject(AppLanguageService);
  private readonly worksuiteAdminConfigService = inject(WorksuiteAdminConfigService);

  /** Wert `device` = Gerätesprache, sonst eine fest gewählte Sprache. */
  protected readonly languageOptions = [
    { label: 'GERAETESPRACHE', value: MitarbeitendeMenuPageComponent.DEVICE_LANGUAGE },
    ...APP_LANGUAGE_OPTIONS,
  ];
  protected readonly selectedLanguageOption = computed(
    () => this.appLanguageService.languageOverride() ?? MitarbeitendeMenuPageComponent.DEVICE_LANGUAGE,
  );

  protected user = signal<WorkUser | undefined>(undefined);
  protected hasMontageplanRead = toSignal(this.store.select(ProfileState.hasPermission(WorkPermissionsEnum.MontageplanREAD)));
  private hasProspectRead = toSignal(this.store.select(ProfileState.hasPermission(WorkPermissionsEnum.ProspectREAD)));
  /** Akquiseobjekte sind ein worksuite-admin-Modul; fail-open wie im work-office, solange die Modul-Liste fehlt. */
  protected showAkquise = computed(
    () => !!this.hasProspectRead() && !this.worksuiteAdminConfigService.isModuleDisabled(WorksuiteModuleKeyEnum.Akquiseobjekte),
  );

  ngOnInit() {
    // Die App lädt die Modul-Liste sonst nirgends; ohne Netz bleibt die gecachte Liste aktiv.
    this.worksuiteAdminConfigService.load().then();
    this.store.select(ProfileState.profile).subscribe((profile) => {
      if (profile) {
        this.user.set((profile as any)?.meta);
      }
    });
  }

  protected async changeLanguage(value: string): Promise<void> {
    await this.appLanguageService.setLanguageOverride(LanguageService.toAppLanguage(value));
  }

  protected async logout(): Promise<void> {
    try {
      await lastValueFrom(this.workAuthenticationV2Service.authenticationV2ControllerLogout({ body: {} }));
    } catch (callException) {
      this.exceptionHandlingService.handleExceptionWithAlert('AuthLogout', callException);
    }
    this.store.dispatch(new StateReset(ProfileState));
    await this.router.navigateByUrl('/');
  }
}
